import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Pencil, Trash2, Search, ShieldCheck, Shirt } from "lucide-react";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";
import { Skeleton } from "@/components/ui/skeleton";
import { api, imageUrl, formatINR, BEAUTY_CAT, FASHION_CAT, THRIFT_CAT } from "@/lib/lamka";
import { withAdminUnlock, adminDeleteListing } from "@/lib/admin";
import { useAdminPassword } from "@/components/admin/AdminPassword";
import { EditListingDialog } from "@/components/admin/EditListingDialog";
import { ThriftUploadDialog } from "@/components/admin/ThriftUploadDialog";
import { useAuth } from "@/context/AuthContext";
import { usePageTitle } from "@/hooks/usePageTitle";

const UNDO_MS = 5000;
const CATS = [["all", "All"], ["beauty", "Beauty"], ["fashion", "Fashion"], ["thrift", "Thrift"], ["other", "Other"]];
const toList = (d) => (Array.isArray(d) ? d : d?.listings || d?.items || []);
const catOf = (c) => (c === BEAUTY_CAT ? "beauty" : c === FASHION_CAT ? "fashion" : c === THRIFT_CAT ? "thrift" : "other");

export default function AdminListings() {
  usePageTitle("Admin · Listings");
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [askPassword, passwordDialog] = useAdminPassword();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const pending = useRef(new Map());

  useEffect(() => {
    if (!isAdmin) return;
    withAdminUnlock(() => api.get("/admin/listings"), askPassword)
      .then((r) => toList(r.data))
      .catch(() => api.get("/listings").then((r) => toList(r.data)))
      .then((list) => setItems(list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))))
      .catch(() => toast.error("Couldn't load listings."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Send any deletes still waiting on undo if the admin leaves the page
  useEffect(() => () => pending.current.forEach((p) => { clearTimeout(p.timer); p.run(); }), []);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((it) => (cat === "all" || catOf(it.category) === cat) && (!s || `${it.title} ${it.seller_name || ""} ${it.category} ${it.location || ""}`.toLowerCase().includes(s)));
  }, [items, q, cat]);

  if (!authLoading && (!user || isAdmin === false)) return <Navigate to={user ? "/" : "/login"} replace />;

  const remove = (item) => {
    const index = items.findIndex((x) => x.id === item.id);
    setItems((l) => l.filter((x) => x.id !== item.id));
    const run = () => {
      pending.current.delete(item.id);
      withAdminUnlock(() => adminDeleteListing(item.id), askPassword).catch((e) => {
        if (e.message !== "cancelled") toast.error(e?.response?.data?.detail || `Couldn't delete “${item.title}”.`);
        setItems((l) => (l.some((x) => x.id === item.id) ? l : [...l.slice(0, index), item, ...l.slice(index)]));
      });
    };
    const timer = setTimeout(run, UNDO_MS);
    pending.current.set(item.id, { timer, run });
    toast(`Deleted “${item.title}”`, {
      duration: UNDO_MS,
      action: {
        label: "Undo",
        onClick: () => {
          clearTimeout(timer);
          pending.current.delete(item.id);
          setItems((l) => [...l.slice(0, index), item, ...l.slice(index)]);
        },
      },
    });
  };

  return (
    <div data-testid="admin-listings-page" className="bg-[#FBF9F5] min-h-screen">
      <NavBar />
      <main className="px-4 sm:px-8 lg:px-12 pt-36 lg:pt-40 pb-24">
        <p className="eyebrow mb-3 flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Admin</p>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
          <h1 className="font-display text-4xl sm:text-5xl text-[#0F0F0F]">All Listings</h1>
          <div className="flex flex-wrap items-center justify-between sm:justify-start gap-3 sm:gap-5">
            <p data-testid="admin-listings-count" className="font-mono text-xs uppercase tracking-[0.2em] text-[#71717A] whitespace-nowrap">{visible.length} of {items.length} posts</p>
            <button data-testid="upload-thrift-item-btn" onClick={() => setUploading(true)} className="h-11 px-4 sm:px-5 whitespace-nowrap rounded-full bg-[#0F0F0F] text-[#FBF9F5] font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.15em] sm:tracking-[0.22em] flex items-center gap-2 hover:bg-[#C5A059] hover:text-[#0F0F0F] transition-colors">
              <Shirt className="h-4 w-4" strokeWidth={1.5} /> Upload Thrift Item
            </button>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <label className="flex items-center gap-2 flex-1 bg-white border border-[#0F0F0F]/15 focus-within:border-[#C5A059] px-3">
            <Search className="h-4 w-4 text-[#71717A]" />
            <input data-testid="admin-listings-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, seller, category or location" className="flex-1 bg-transparent outline-none py-2.5 text-sm" />
          </label>
          <div className="flex gap-5 sm:gap-6 border-b border-[#C5A059]/25 overflow-x-auto no-scrollbar [&>button]:shrink-0 max-[379px]:[mask-image:linear-gradient(to_right,black_85%,transparent)]">
            {CATS.map(([id, label]) => (
              <button key={id} data-testid={`admin-filter-${id}`} onClick={() => setCat(id)} className={`pb-2 -mb-px text-xs uppercase tracking-[0.25em] border-b-2 ${cat === id ? "border-[#C5A059] text-[#0F0F0F]" : "border-transparent text-[#71717A]"}`}>{label}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-none bg-[#F5EFE6]" />)}</div>
        ) : visible.length === 0 ? (
          <p data-testid="admin-listings-empty" className="font-display text-2xl text-[#71717A] py-16">No posts match.</p>
        ) : (
          <ul data-testid="admin-listings-list" className="divide-y divide-[#0F0F0F]/5 bg-white border border-[#C5A059]/25">
            {visible.map((it) => (
              <li key={it.id} data-testid={`admin-row-${it.id}`} className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3">
                <Link to={`/product/${it.id}`} className="h-14 w-12 shrink-0 bg-[#F5EFE6] overflow-hidden">
                  {it.images?.[0] && <img src={imageUrl(it.images[0])} alt="" loading="lazy" className="h-full w-full object-cover" />}
                </Link>
                <div className="min-w-0 flex-1">
                  <Link to={`/product/${it.id}`} className="block text-sm font-medium truncate hover:text-[#B38F46]">{it.title}</Link>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#71717A] truncate">
                    {it.seller_name || "—"} · {it.category}{it.active === false ? " · inactive" : ""}
                  </p>
                </div>
                <div className="hidden md:block text-right shrink-0 w-28">
                  <p className="font-mono text-sm truncate">{formatINR(it.price)}</p>
                  <p className="font-mono text-[10px] text-[#71717A]">{it.created_at && new Date(it.created_at).toLocaleDateString([], { day: "numeric", month: "short", year: "2-digit" })}</p>
                </div>
                <button data-testid={`admin-row-edit-${it.id}`} aria-label="Edit" onClick={() => setEditing(it)} className="p-2.5 border border-[#0F0F0F]/15 hover:border-[#0F0F0F] shrink-0"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`admin-row-delete-${it.id}`} aria-label="Delete" onClick={() => remove(it)} className="p-2.5 border border-red-300 text-red-600 hover:bg-red-600 hover:text-white shrink-0"><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
        )}
      </main>
      <ThriftUploadDialog open={uploading} onOpenChange={setUploading} askPassword={askPassword} onCreated={(it) => setItems((l) => [it, ...l])} />
      <EditListingDialog
        item={editing}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onUpdated={(u) => setItems((l) => l.map((x) => (x.id === u.id ? { ...x, ...u } : x)))}
        askPassword={askPassword}
      />
      {passwordDialog}
    </div>
  );
}
